import type { BallState, MatchState, PlayerState } from './state';
import type { SimConfig } from '../config/simConfig';
import type { PossessionWhy, SimEvent } from './events';
import type { ActionKind, EntityId, PlayerInput } from '../types';
import { DT } from '../config/simConfig';
import { PITCH } from '../config/pitchConfig';
import { BTN } from './input';
import { inOwnBox } from './gk';
import { inCeremony, ballLive } from '../rules/phase';

/** No-touch actions — a player in one of these cannot claim or hold the ball (§5.6). Exported for the
 *  D52 ball-body collision: a player who CANNOT control the ball is a pure obstacle at any speed.
 *
 *  D120 — this set must CONTAIN every input-ignored action (`INPUT_IGNORED_ACTIONS`, actions.ts), and a
 *  test enforces the relation. A player who cannot steer cannot keep the ball: those are the same
 *  moment in the sport, and separating them created a state where you owned a ball you could not play.
 *  The four that used to sit outside it (TACKLE_STAND, TACKLE_SLIDE, SLIDE_RECOVERY, SHOVE) are exactly
 *  the verbs that WIN the ball, so the sim handed it over at the one instant it could not be used. */
export const NO_TOUCH: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'RAGDOLL',
  'PRONE',
  'GETUP',
  'DIVE_FALL',
  'PUNCH_WINDUP',
  'PUNCH_ACTIVE',
  'PUNCH_RECOVERY',
  'KICK_RELEASE',
  'GK_DIVE',
  'GK_SMOTHER',
  'HEADER_JUMP', // D113: a player in the air is not making a ground claim — he heads it or he doesn't
  'TACKLE_STAND', // D120: mid-lunge — he plays the ball, he does not hold it
  'TACKLE_SLIDE', // D120
  'SLIDE_RECOVERY', // D120: on the floor. This is the one the D119 report was actually about.
  'SHOVE', // D120: both arms are busy
]);

/**
 * D80 back-cone shield (§5.6, "protect all dribblers"). True iff `opp` sits inside the carrier's back
 * cone — `dot(carrier.facing, carrier→opp) < −coneDot` — where the carrier's body screens the striding
 * ball. A shielded opponent can neither tackle nor claim the ball from directly behind; he must come
 * around to the side/front. Pure + symmetric (the sim never distinguishes bots from humans, §3).
 */
export function shieldedFromBehind(
  carrierFacing: number,
  carrierPos: { readonly x: number; readonly y: number },
  oppPos: { readonly x: number; readonly y: number },
  coneDot: number,
): boolean {
  const dx = oppPos.x - carrierPos.x;
  const dy = oppPos.y - carrierPos.y;
  const dd = Math.hypot(dx, dy);
  if (dd < 1e-6) return false;
  return (Math.cos(carrierFacing) * dx + Math.sin(carrierFacing) * dy) / dd < -coneDot;
}

/**
 * D119 — the ONE claim-lockout predicate, shared by all three "take" verbs (claim, tackle, trap).
 *
 * `claimCooldownOnly` carries two meanings on the same field. An EntityId bars exactly that player —
 * his own fresh kick / dribble knock-on / trap deflect, or the D89 grace on the man who was just
 * cleanly beaten. `null` bars EVERYONE: that is the D35 spill's "a real 50/50 — BOTH sides wait out
 * the spill". The tackle and trap paths used to test `only === p.id` on their own, which is never true
 * for `null`, so a contested ball gated nobody: since STAND_WINDOW is [2,9], the tackler who caused the
 * spill still had live window ticks and scooped his own spill on the very next tick, standing over it.
 * One predicate at one place, so the three sites cannot drift apart again.
 */
export function claimBarred(b: BallState, id: EntityId): boolean {
  return b.claimCooldownTicks > 0 && (b.claimCooldownOnly === null || b.claimCooldownOnly === id);
}

/**
 * D131 — is the ball's owner a FRESH RECEIVER? Read by the two buttonless steals (the D40 body-block and
 * the barge), which have no claim to be barred and so cannot go through `claimBarred`.
 *
 * The window is the same one `claimBarred` sees: `claimCooldownTicks > 0` with a cooldown that names
 * NOBODY. That field pair has two meanings and the wire cannot tell them apart — but the ball's own
 * possession state can, and it is the only discriminator needed: written while the ball is POSSESSED it
 * is this grace, written while it is LOOSE it is the D35 spill's "both sides wait". Every caller here
 * asks about a carrier, so the reading is unambiguous by construction.
 */
export function inReceptionGrace(b: BallState): boolean {
  return b.claimCooldownTicks > 0 && b.claimCooldownOnly === null;
}

/**
 * D131 — is this gather a RECEPTION (⇒ arm the grace), or is he re-collecting a ball he himself put
 * loose? A dribble knock-on and a player's own kick both stamp `claimCooldownOnly` with his id AND
 * leave him as the last toucher; nothing else does both. Without this test a sprinting dribbler re-arms
 * the grace every DRIBBLE_KNOCK_PERIOD ticks and becomes permanently untacklable — grace chaining into
 * immunity, which is a worse bug than the one being fixed.
 *
 * A D53 stride touch also leaves him as last toucher, but writes no cooldown ("the toucher strides onto
 * his own touch next tick"), so the cooldown still names whoever kicked it — which is exactly what
 * separates meeting a pass from knocking your own ball ahead.
 */
function isReception(b: BallState, id: EntityId): boolean {
  return !(b.claimCooldownOnly === id && b.lastTouch !== null && b.lastTouch.entity === id);
}

/** D131 — arm the fresh-receiver window on a gather, unless he is re-collecting his own loose ball.
 *  Call it BEFORE `lastTouch` is restamped, or the reception test reads its own outcome. */
export function armReceptionGrace(b: BallState, id: EntityId, cfg: SimConfig): void {
  if (!isReception(b, id)) return;
  b.claimCooldownTicks = cfg.possession.RECEIVE_GRACE_TICKS;
  b.claimCooldownOnly = null;
}

/**
 * D129 — where a DEAD ball sits while its taker holds it: the same point the client already renders a
 * carried ball at (`netGame.ts::ballRenderPos`, pos + facing·CARRY_POINT), clamped inside the playing
 * area. The clamp is the touchline case: a throw-in taker standing on the line who sweeps his aim
 * outward would otherwise swing the ball out of play before he has kicked it.
 *
 * The offset follows FACING on purpose (that is what makes the render truthful), and the carry below
 * writes the ball's VELOCITY from the carrier rather than from the position delta — so lining a kick
 * up moves the ball on screen without ever making it "live". Aiming is not taking.
 */
function deadBallCarryPoint(state: MatchState, p: PlayerState, cfg: SimConfig): { x: number; y: number } {
  const pitch = PITCH[state.mode.teamSize];
  const hx = pitch.length / 2 - cfg.ball.RADIUS;
  const hy = pitch.width / 2 - cfg.ball.RADIUS;
  const x = p.pos.x + Math.cos(p.facing) * cfg.possession.CARRY_POINT;
  const y = p.pos.y + Math.sin(p.facing) * cfg.possession.CARRY_POINT;
  return { x: x < -hx ? -hx : x > hx ? hx : x, y: y < -hy ? -hy : y > hy ? hy : y };
}

/**
 * D129 — step 6a for a DEAD ball that somebody HOLDS: the taker carries it.
 *
 * Before this, `resolvePossession` was PLAY-gated, so a possessed dead ball had no carry, no leash and
 * no strip. The ball stayed pinned at the spot while its "possessor" walked off; `applyKicks` refused
 * every kick from beyond `kick.REACH` — after `applyActions` had already played the full windup — and
 * `takerHolds` vetoed the phase flip until the RESTART_MAX_TICKS timeout. The clip's free kick is the
 * arithmetic: FOUL 302 → whistle 347 → claim 461 → PLAY only at 647 = 302 + 45 + 300.
 *
 * Deliberately NOT the full possession pass: a dead ball has no duel (the D62c lesson, kept). No
 * body-block, no knock-on, no claim — those all live in `resolvePossession`, which this replaces
 * outside PLAY. What comes across is the keep/lose half: the LOSE_DIST leash and the NO_TOUCH strip.
 *
 * PT4-P18 — a ball stripped off a wandering or ragdolled taker is put DOWN, not played: its velocity is
 * zeroed so `ballLive` stays false and the restart stays pending. Going live off a LOSS would read as a
 * bug; only a deliberate touch takes a restart live. The stripped man carries the claim cooldown so the
 * strip and the taker magnetism below cannot ping-pong on alternating sub-steps.
 */
export function resolveDeadBallCarry(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const b = state.ball;
  if (b.possessor === null) return;
  const p = state.players[b.possessor];
  const lost = !p || p.sentOff || NO_TOUCH.has(p.action) || Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) > cfg.possession.LOSE_DIST;
  if (lost) {
    const why: PossessionWhy = !p || p.sentOff ? 'SENT_OFF' : NO_TOUCH.has(p.action) ? 'NO_TOUCH' : 'LEASH';
    b.possessor = null;
    b.vel = { x: 0, y: 0, z: 0 }; // PT4-P18: put down, not played
    if (p) {
      b.claimCooldownTicks = cfg.possession.KNOCK_FREE_TICKS;
      b.claimCooldownOnly = p.id;
    }
    events.push({ t: 'POSSESSION', entity: null, why });
    return;
  }
  // D83: during the ceremony the ball is placed and nothing moves it — the taker may walk up to it (and
  // walk past the leash, above, losing it) but play never resumes mid-cutscene.
  if (inCeremony(state, cfg)) {
    b.vel = { x: 0, y: 0, z: 0 };
    return;
  }
  const c = deadBallCarryPoint(state, p, cfg);
  b.pos.x = c.x;
  b.pos.y = c.y;
  b.pos.z = 0;
  // velocity comes from the CARRIER, never from the position delta: a turn on the spot moves the ball
  // without moving the restart on. Walking with it is what exceeds TRAPPED_SPEED ⇒ ballLive ⇒ PLAY.
  b.vel.x = p.vel.x;
  b.vel.y = p.vel.y;
  b.vel.z = 0;
}

/**
 * D122 — step 6b for a DEAD ball: the kicking team may gather a pending restart once the whistle has
 * gone. Deliberately narrow — a claim and nothing else. No knock-on, no body block, no tackle
 * interaction: a dead ball has no duel to resolve, and running the full `resolvePossession` here is
 * what re-opened the D62c "ten-second freeze" class. Claiming IS taking: `apply.ts`'s `takerHolds`
 * grace flips the phase to PLAY the moment possession leaves the taker or he carries the ball away.
 *
 * D129 — the DESIGNATED taker gathers from `rules.TAKER_CLAIM_RADIUS` (2.5 m) instead of CLAIM_RADIUS
 * (0.9 m). The clip's taker stood 2.0–2.1 m from the ball for ~3.1 s pressing to no effect, then
 * claimed and kicked within one tick of stepping inside 0.9 m — the radius, not the button, was the
 * bug. Nearest-eligible still wins, so a teammate standing over it beats a taker two metres out.
 * And the ball SNAPS to his carry point: it genuinely follows him from here, which is what turns the
 * client's glued render from a lie into the truth.
 *
 * Excluded: the whole defending team, everyone during the ceremony, and PENALTY (struck, not dribbled).
 */
export function resolveDeadBallClaim(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const b = state.ball;
  const r = state.restart;
  if (r === null || b.possessor !== null) return;
  if (r.kind === 'PENALTY' || inCeremony(state, cfg)) return;
  if (b.pos.z >= cfg.possession.CLAIM_Z) return;
  // …and only while the ball is genuinely DEAD. Once it has been played it is live: the phase flips to
  // PLAY at step 8 this same tick, and re-gathering it here would let the taker vacuum his own kick
  // back off the turf (and, since this claim zeroes velocity, kill the restart outright).
  if (ballLive(state, cfg)) return;

  const P = cfg.possession;
  const mul = cfg.conditions[state.conditions].CLAIM_RADIUS_MUL;
  const radius = P.CLAIM_RADIUS * mul;
  const takerRadius = cfg.rules.TAKER_CLAIM_RADIUS * mul;
  let bestId = -1;
  let bestDist = Infinity;
  for (const p of state.players) {
    if (p.sentOff || p.team !== r.team || NO_TOUCH.has(p.action)) continue;
    // D129: the same one lockout predicate the live claim uses (D119) — without it the leash strip
    // above and the magnetism here would trade the ball back and forth on every tick.
    if (claimBarred(b, p.id)) continue;
    const d = Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y);
    if (d < (p.id === r.taker ? takerRadius : radius) && d < bestDist) {
      // strict < over ascending ids ⇒ exact ties keep the lower EntityId (§5.6 tie-break)
      bestDist = d;
      bestId = p.id;
    }
  }
  if (bestId < 0) return;
  const claimant = state.players[bestId]!;
  b.possessor = bestId;
  const c = deadBallCarryPoint(state, claimant, cfg); // D129: it is at his feet now, and it stays there
  b.pos.x = c.x;
  b.pos.y = c.y;
  b.pos.z = 0;
  b.vel = { x: 0, y: 0, z: 0 }; // a dead ball is at rest; gathering it does not launch it
  b.kickedBy = null;
  b.shotPending = false;
  armReceptionGrace(b, bestId, cfg); // D131/PT4-P22: a restart gather is a gather like any other
  b.lastTouch = { entity: bestId, team: claimant.team, tick: state.tick };
  events.push({ t: 'POSSESSION', entity: bestId, why: 'CLAIM' });
}

/**
 * Step 6 of §5.1 — possession by soft magnetism (§5.6). Keeps or releases the current carrier, then
 * lets the nearest eligible player claim a slow, low, nearby loose ball. The carried ball is held to a
 * carry point ahead of the carrier by a critically-damped spring (the free-ball integrator is skipped
 * for a possessed ball — see step.ts). Mutates `state`; emits POSSESSION on change.
 */
export function resolvePossession(state: MatchState, cfg: SimConfig, events: SimEvent[], inputs?: readonly PlayerInput[]): void {
  const P = cfg.possession;
  const cond = cfg.conditions[state.conditions];
  const b = state.ball;

  // --- keep / lose the current carrier ---
  if (b.possessor !== null) {
    const p = state.players[b.possessor];
    const lost =
      !p ||
      p.sentOff ||
      NO_TOUCH.has(p.action) ||
      Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) > P.LOSE_DIST;
    if (lost) {
      // D119: `lost` is one boolean over three genuinely different causes — name the one that fired, so
      // a log distinguishes "he was ragdolled off it" from "the ball ran away from him".
      const why: PossessionWhy = !p || p.sentOff ? 'SENT_OFF' : NO_TOUCH.has(p.action) ? 'NO_TOUCH' : 'LEASH';
      b.possessor = null; // released — ball keeps its velocity (live loose ball)
      events.push({ t: 'POSSESSION', entity: null, why });
    } else if (
      // D40 body-block: the carried ball touching an OPPONENT's body breaks the dribble — running
      // head-on into a set defender loses the ball, no button needed. SHIELD (the carrier's counter-
      // verb) and in-hands GK_HOLD are exempt; a containing defender presents a wider body.
      p.action !== 'GK_HOLD' &&
      p.action !== 'SHIELD' &&
      // D131: …and not while he is still receiving it. The clip's two "instant steals" were both this
      // verb, 6–7 ticks after the pass arrived. The body-block has no claim to bar, so it reads the
      // fresh-receiver window directly rather than through `claimBarred`.
      !inReceptionGrace(b) &&
      ((): boolean => {
        const basePad = cfg.collision.CAPSULE_R + cfg.ball.RADIUS + P.BODY_BLOCK_PAD;
        for (const o of state.players) {
          if (o.team === p.team || o.sentOff || NO_TOUCH.has(o.action)) continue;
          const dx = o.pos.x - p.pos.x;
          const dy = o.pos.y - p.pos.y;
          const dd = Math.hypot(dx, dy) || 1;
          // the CARRIER must be driving INTO the defender — static tight marking never strips a standing
          // carrier (that's the tackle/duel system's job). D131: the threshold is config now (it was an
          // inline 1.5, a standing §17 breach) and tightened, so only a genuine drive costs you the ball.
          const closing = (p.vel.x * dx + p.vel.y * dy) / dd;
          // D132: a defender who is BLOCKing has SET himself — the user's spec is "if they come too
          // close, high probability of the ball being deflected away". So against a braced body the bar
          // is the RELATIVE closing speed (his backpedal counts) at a lower threshold. The carrier's own
          // motion is still required: `closing` is computed from the CARRIER's velocity alone above, so
          // an advancing blocker walking into a standing man can never strip him.
          const rel = o.action === 'BLOCK' ? closing - (o.vel.x * dx + o.vel.y * dy) / dd : closing;
          const bar = o.action === 'BLOCK' ? P.BLOCK_REL_CLOSING_MIN : P.BODY_BLOCK_CLOSING_MIN;
          if (rel < bar) continue;
          // …and the CARRIER must have done at least half of that closing himself. Otherwise an ADVANCING
          // blocker could walk into a standing man and take it off him, which is the tight-marking steal
          // D44 removed on purpose. Dimensionless, so it needs no threshold of its own: "you ran into him
          // at least as much as he ran into you".
          if (closing * 2 < rel) continue;
          // D44: FRONTAL geometry only — the carrier must be heading roughly AT him, not brushing past
          // his shoulder. D131: the cone came in from ~60° to ~49.5°, also out of the inline literals.
          if (closing / (Math.hypot(p.vel.x, p.vel.y) || 1) < P.BODY_BLOCK_FRONT_DOT) continue;
          const pad = o.action === 'BLOCK' ? basePad * P.CONTAIN_BLOCK_MUL : basePad; // D82: a braced BLOCK body presents the wider block
          if (Math.hypot(b.pos.x - o.pos.x, b.pos.y - o.pos.y) < pad) {
            b.lastTouch = { entity: o.id, team: o.team, tick: state.tick }; // his legs took it
            return true;
          }
        }
        return false;
      })()
    ) {
      b.possessor = null; // blocked off the defender — live loose ball (physics bounces it off his body)
      b.claimCooldownTicks = 2;
      b.claimCooldownOnly = p.id; // the blocker may play it instantly
      b.kickedBy = null;
      events.push({ t: 'POSSESSION', entity: null, why: 'BODY_BLOCK' });
    } else if (
      // D39 dribble knock-on: a fast-moving carrier pushes the ball a touch ahead every
      // DRIBBLE_KNOCK_PERIOD ticks (staggered by entity id — no league-wide metronome). The ball is
      // genuinely LOOSE until the next claim, immediately claimable by ANYONE — a defender closer to
      // the rolling ball wins it. Never in hands (GK_HOLD), never on a dead ball.
      // D44 risk dial: knock-ons gate on the SPRINT LATCH, not raw speed. Jogging/running (even at full
      // base 8 m/s) keeps close control ALWAYS — the old raw-speed gate (min 6.5 < base max 8.0) knocked
      // the ball loose on every plain run ("lost the ball for no reason"). Only a held sprint = long touches.
      p.action !== 'GK_HOLD' &&
      state.phase === 'PLAY' &&
      p.sprinting &&
      // D47 first-touch grace: a fresh claim SETTLES the ball for a beat — a receiver sprinting to
      // meet a pass no longer punts it loose the instant it arrives (the probe showed this, not raw
      // pace, was the "lost the ball on reception" path). Claims stamp lastTouch, so no new state.
      !(b.lastTouch !== null && b.lastTouch.entity === p.id && state.tick - b.lastTouch.tick < P.DRIBBLE_KNOCK_GRACE_TICKS) &&
      Math.hypot(p.vel.x, p.vel.y) >= P.DRIBBLE_KNOCK_SPEED_MIN &&
      // D81 straight-run gate: only knock while the carrier is running roughly STRAIGHT — the velocity
      // heading agrees with where he's steering (input). Mid-turn (velDir·inputDir < ALIGN_MIN) the ball
      // stays GLUED, so a jink/turn/reversal never knocks it sideways or behind (the D66 probe: turns
      // lost the ball, straight didn't). A bot with no input frame falls through (undefined ⇒ allowed).
      ((): boolean => {
        const inp = inputs?.[p.id];
        const mvLen = inp ? Math.hypot(inp.moveX, inp.moveY) : 0;
        const vsp = Math.hypot(p.vel.x, p.vel.y);
        if (!inp || mvLen < 0.3 || vsp < 1e-4) return true;
        return (p.vel.x * inp.moveX + p.vel.y * inp.moveY) / (vsp * mvLen) >= P.DRIBBLE_KNOCK_ALIGN_MIN;
      })() &&
      (state.tick + p.id * 5) % P.DRIBBLE_KNOCK_PERIOD === 0
    ) {
      // D81: knock RELATIVE to the carrier's motion and along the MOVEMENT direction (velocity heading —
      // NOT facing, which lagged on turns and shot the touch sideways). vel = carrier.vel + moveDir·PUSH
      // so the ball ALWAYS leads by a pace-proportional distance and oscillates FORWARD (the old
      // speed × LEAD_MUL over-scaled the whole vector — a turn sent a 1.45× copy of the old heading away).
      const sp = Math.hypot(p.vel.x, p.vel.y);
      const ux = p.vel.x / sp;
      const uy = p.vel.y / sp;
      b.possessor = null;
      b.pos.x = p.pos.x + ux * P.CARRY_POINT;
      b.pos.y = p.pos.y + uy * P.CARRY_POINT;
      b.vel.x = p.vel.x + ux * P.DRIBBLE_KNOCK_PUSH;
      b.vel.y = p.vel.y + uy * P.DRIBBLE_KNOCK_PUSH;
      b.claimCooldownTicks = 4;
      b.claimCooldownOnly = p.id; // D43: only the CARRIER waits — a defender ahead takes it instantly
      b.kickedBy = null; // a dribble touch is NOT a pass
      b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
      events.push({ t: 'POSSESSION', entity: null, why: 'KNOCK_ON' });
    } else {
      // critically-damped carry spring toward the carry point (pos + facing·CARRY_POINT), tracked in
      // the carrier's moving frame (feedforward = player velocity). Exact analytic update (no Euler
      // error): for ẍ = −2ω ẋ − ω² x, x(dt) = (A + B·dt)e^(−ω dt), A = x0, B = v0 + ω x0.
      // D48: a ball IN HANDS (GK_HOLD) rides the BODY, not the boot — the facing-offset carry parked it
      // wherever the keeper happened to face (goal-side after a save on the line, where the spring could
      // even walk a caught ball over the line); the release point is chosen at kick time instead.
      const off = p.action === 'GK_HOLD' ? 0 : P.CARRY_POINT;
      const carryX = p.pos.x + Math.cos(p.facing) * off;
      const carryY = p.pos.y + Math.sin(p.facing) * off;
      const omega = P.SPRING_OMEGA * cond.SPRING_MUL;
      const e = Math.exp(-omega * DT);
      // Track the carry point in ITS moving frame (velocity = carrier velocity, feed-forward). The
      // relative origin is the START-of-tick carry position (carryX − carrierVel·DT).
      const rx = b.pos.x - (carryX - p.vel.x * DT);
      const rvx = b.vel.x - p.vel.x;
      const bx = rvx + omega * rx;
      b.pos.x = carryX + (rx + bx * DT) * e;
      b.vel.x = p.vel.x + (bx - omega * (rx + bx * DT)) * e;

      const ry = b.pos.y - (carryY - p.vel.y * DT);
      const rvy = b.vel.y - p.vel.y;
      const by = rvy + omega * ry;
      b.pos.y = carryY + (ry + by * DT) * e;
      b.vel.y = p.vel.y + (by - omega * (ry + by * DT)) * e;

      b.pos.z = 0;
      b.vel.z = 0;
    }
  }

  // --- claim a loose ball (respecting the knock-free window) ---
  // D83 ceremony: the ball is UNTAKEABLE until the ceremony ends — nobody claims a dead ball on the spot
  // (not even the taker); he walks up but may not gather it until the whistle.
  if (b.possessor === null && !inCeremony(state, cfg) && b.pos.z < P.CLAIM_Z && Math.hypot(b.vel.x, b.vel.y, b.vel.z) < P.CLAIM_MAX_SPEED) {
    const radius = P.CLAIM_RADIUS * cond.CLAIM_RADIUS_MUL;
    // D47: stepping INTO a pass reaches a little further than chasing a fleeing ball — receivers meet
    // the ball, they don't tiptoe up to it
    const speed2 = Math.hypot(b.vel.x, b.vel.y);
    // D122: the dead-ball case moved out to `resolveDeadBallClaim` — this pass only ever runs in PLAY
    // now (step.ts picks the variant), so the old `deadBall` branch here was unreachable either way.
    let bestId = -1;
    let bestDist = Infinity;
    let gkId = -1; // D55: an eligible in-box KEEPER — he outranks his own teammates on the claim
    for (const p of state.players) {
      if (p.sentOff || NO_TOUCH.has(p.action)) continue;
      // D43: the claim cooldown blocks only its subject entity (the kicker/knocker) — everyone else
      // (a receiver stepping onto a pass!) controls the ball the moment it's in reach. D119: …and a
      // cooldown that names NOBODY (a D35 spill) blocks the whole pitch — see claimBarred.
      if (claimBarred(b, p.id)) continue;
      // D80 back-cone shield on the CLAIM: during a dribbler's knock window (a live touch by a specific
      // carrier, kickedBy null), an OPPONENT inside that carrier's back cone can't nip the striding ball
      // — he must come around. The carrier himself is already gated by the cooldown above. Symmetric.
      // D100: …but ONLY when the cooldown subject really is the dribbler. `claimCooldownOnly` is written by
      // five different sites, and two of them name the player who just LOST the ball (the D89
      // possession-grace, and the D40 barge). Shielding him inverts the rule: the cone was computed from the
      // BEATEN player's facing and used to refuse the side that had just won the ball. A knocker is always
      // also the ball's LAST TOUCHER — he pushed it ahead of himself — whereas after a steal the last touch
      // belongs to the winner, so the pairing separates the cases without a new state field.
      if (b.kickedBy === null && b.claimCooldownTicks > 0 && b.claimCooldownOnly !== null && b.lastTouch?.entity === b.claimCooldownOnly) {
        const knocker = state.players[b.claimCooldownOnly];
        if (knocker && knocker.team !== p.team && shieldedFromBehind(knocker.facing, knocker.pos, p.pos, P.SHIELD_CONE_DOT)) continue;
      }
      const d = Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y);
      // …but NOT for a keeper in his own box: with the bonus his feet would outreach his hands
      // (1.215 m > STANDING_REACH 1.1) and foot-trap shots the auto-save should catch (D39).
      const incoming =
        speed2 > 4 && b.vel.x * (p.pos.x - b.pos.x) + b.vel.y * (p.pos.y - b.pos.y) > 0 && !(p.isGk && inOwnBox(state, p));
      const eff = incoming ? radius * P.RECEIVE_RADIUS_MUL : radius;
      // strict < over ascending ids ⇒ exact ties keep the lower EntityId (§5.6 tie-break)
      if (d < eff) {
        if (p.isGk && inOwnBox(state, p) && gkId < 0) gkId = p.id;
        if (d < bestDist) {
          bestDist = d;
          bestId = p.id;
        }
      }
    }
    // D55: the keeper has PRECEDENCE over his own teammates in his box — a defender must never poach a
    // ball his keeper is on ("the keeper should always have precedent on taking possession"). An
    // OPPONENT winning by distance still wins — that's a fair race to the ball.
    if (gkId >= 0 && bestId >= 0 && bestId !== gkId && state.players[bestId]!.team === state.players[gkId]!.team) bestId = gkId;
    if (bestId >= 0) {
      const claimant = state.players[bestId]!;
      // D53 directional first touch (flag: TOUCH_DIRECTIONAL): a MOVING receiver meeting a FAST ball
      // takes it INTO HIS STRIDE — one touch pushes it along his movement and the ball stays live for a
      // beat (he re-claims it a few ticks later, relative pace now small). The ball reads as its own
      // object you keep meeting, not an appendage that snaps to the boot. A held kick button (one-touch
      // intent, D53) takes precedence — those want possession NOW to fire first-time.
      const inp = inputs?.[bestId];
      const mvLen = inp ? Math.hypot(inp.moveX, inp.moveY) : 0;
      const relSpeed = Math.hypot(b.vel.x - claimant.vel.x, b.vel.y - claimant.vel.y);
      // D64: a TRAP press means "kill the ball NOW" — it must claim, never stride-push (the trap verb
      // and the stride touch were fighting over the same reception).
      const wantsKick = inp !== undefined && (inp.buttons & (BTN.PASS | BTN.THROUGH | BTN.SHOOT | BTN.TRAP)) !== 0;
      // D55: NEVER stride-touch toward the OWN goal from the own half — a defender chasing a ball
      // rolling at his net was pushing it goal-ward touch after touch ("marching it into the goal");
      // he cushion-claims instead and the defusal logic clears it.
      const atkDir = state.attackDir[claimant.team]!;
      const goalWardStride = inp !== undefined && mvLen > 0.3 && -atkDir * (inp.moveX / (mvLen || 1)) > 0.3 && atkDir * claimant.pos.x < 0;
      // D64: the stride touch is for taking a ball ONWARD — a receiver moving roughly WITH its travel.
      // Fired against a receiver running to MEET the pass (the normal head-on reception), the push went
      // INTO the ball's face: his first touch fired it straight back at the passer at movement+PUSH
      // speed — a pixel-perfect "instant return pass" made of possession physics, with the touch landing
      // the D50 pass-follow so control jumped to him as the ball rolled away. Require alignment.
      const bSpd2 = Math.hypot(b.vel.x, b.vel.y);
      const strideAligned =
        inp !== undefined && mvLen > 0.3 && bSpd2 > 1e-6 && (inp.moveX / mvLen) * (b.vel.x / bSpd2) + (inp.moveY / mvLen) * (b.vel.y / bSpd2) > 0.2;
      if (P.TOUCH_DIRECTIONAL === 1 && !wantsKick && !goalWardStride && strideAligned && inp && mvLen > 0.3 && relSpeed > 4 && claimant.action !== 'GK_HOLD' && !claimant.isGk) {
        const ux = inp.moveX / mvLen;
        const uy = inp.moveY / mvLen;
        b.vel.x = claimant.vel.x + ux * P.TOUCH_PUSH_SPEED;
        b.vel.y = claimant.vel.y + uy * P.TOUCH_PUSH_SPEED;
        b.vel.z *= P.TOUCH_CUSHION;
        b.kickedBy = null;
        b.shotPending = false;
        b.lastTouch = { entity: bestId, team: claimant.team, tick: state.tick }; // a real TOUCH — reception logic keys on this
        // no possession, no cooldown: the toucher strides onto his own touch next tick
      } else {
        b.possessor = bestId;
        b.kickedBy = null; // resolved — the flight is over
        // D47 cushioned first touch: kill the incoming pace toward the receiver's own velocity, or the
        // carry leash (LOSE_DIST) loses a crisp pass again one tick after "receiving" it — the single
        // biggest "players often lose the ball" cause.
        b.vel.x = claimant.vel.x + (b.vel.x - claimant.vel.x) * P.TOUCH_CUSHION;
        b.vel.y = claimant.vel.y + (b.vel.y - claimant.vel.y) * P.TOUCH_CUSHION;
        b.vel.z *= P.TOUCH_CUSHION;
        armReceptionGrace(b, bestId, cfg); // D131 — read BEFORE lastTouch moves under it
        b.lastTouch = { entity: bestId, team: claimant.team, tick: state.tick };
        b.shotPending = false; // a collected ball is no longer a live shot (§6.2 MISS)
        events.push({ t: 'POSSESSION', entity: bestId, why: 'CLAIM' });
      }
    }
  }
}
